import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Palette, Check, RefreshCw, Sun, Moon, Sparkles } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useToast } from '../../hooks/use-toast';

const ThemeManagement = () => {
  const { toast } = useToast();
  const { 
    currentTheme, 
    theme, 
    themes, 
    themeOrder, 
    autoRotate, 
    changeTheme, 
    toggleAutoRotate 
  } = useTheme();

  const handleThemeChange = (themeId) => {
    changeTheme(themeId);
    toast({
      title: "Theme Updated",
      description: `Switched to ${themes[themeId].name} theme`
    });
  };

  const handleAutoRotateToggle = (enabled) => {
    toggleAutoRotate(enabled);
    toast({
      title: enabled ? "Auto-Rotate Enabled" : "Auto-Rotate Disabled",
      description: enabled 
        ? "Theme will change daily for a fresh experience" 
        : "Theme will stay fixed"
    });
  };

  // Preview colors for each theme
  const getPreviewColors = (themeId) => {
    const colorMap = {
      classic: ['bg-amber-500', 'bg-slate-700', 'bg-amber-600'],
      ocean: ['bg-blue-500', 'bg-cyan-500', 'bg-blue-700'],
      forest: ['bg-emerald-500', 'bg-teal-500', 'bg-emerald-700'],
      sunset: ['bg-orange-500', 'bg-rose-500', 'bg-orange-700'],
      midnight: ['bg-violet-500', 'bg-purple-500', 'bg-indigo-700']
    };
    return colorMap[themeId] || colorMap.classic;
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold flex items-center gap-2">
            <Palette className="h-5 w-5 md:h-6 md:w-6" />
            Theme Management
          </h2>
          <p className="text-xs md:text-sm text-slate-600 mt-1">
            Customize the look and feel of your portfolio
          </p>
        </div>
        <Badge variant="outline" className="flex items-center gap-2 w-fit">
          <div className={`w-3 h-3 rounded-full ${getPreviewColors(currentTheme)[0]}`}></div>
          {themes[currentTheme]?.name || 'Classic'}
        </Badge>
      </div>

      {/* Auto-Rotate Setting */}
      <Card className="bg-gradient-to-r from-violet-50 to-purple-50 border-violet-200">
        <CardContent className="p-4 md:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-violet-100 rounded-lg">
                <RefreshCw className={`h-5 w-5 text-violet-600 ${autoRotate ? 'animate-spin' : ''}`} style={{ animationDuration: '3s' }} />
              </div>
              <div>
                <h3 className="font-semibold text-sm md:text-base">Daily Theme Rotation</h3>
                <p className="text-xs md:text-sm text-slate-600 mt-1">
                  Automatically change themes daily for a fresh user experience
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-slate-500">{autoRotate ? 'On' : 'Off'}</span>
              <Switch
                checked={autoRotate}
                onCheckedChange={handleAutoRotateToggle}
              />
            </div>
          </div>
          {autoRotate && (
            <div className="mt-4 p-3 bg-white/50 rounded-lg text-xs md:text-sm text-slate-600">
              <Sparkles className="h-4 w-4 inline mr-2 text-violet-500" />
              Tomorrow's theme: <strong>{themes[themeOrder[(themeOrder.indexOf(currentTheme) + 1) % themeOrder.length]]?.name}</strong>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Theme Selection Grid */}
      <div className="grid gap-3 md:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
        {themeOrder.map((themeId) => {
          const t = themes[themeId];
          const isActive = currentTheme === themeId;
          const previewColors = getPreviewColors(themeId);
          
          return (
            <Card 
              key={themeId}
              className={`cursor-pointer transition-all hover:shadow-lg ${
                isActive 
                  ? 'ring-2 ring-offset-2 ring-amber-500 border-amber-300' 
                  : 'hover:border-slate-300'
              }`}
              onClick={() => handleThemeChange(themeId)}
            >
              <CardContent className="p-4">
                {/* Theme Preview */}
                <div className={`h-24 md:h-32 rounded-lg mb-3 overflow-hidden bg-gradient-to-br ${t.gradients.hero} relative`}>
                  {/* Mini preview elements */}
                  <div className="absolute inset-0 p-3">
                    <div className="h-2 w-16 bg-white/30 rounded mb-2"></div>
                    <div className="h-1 w-24 bg-white/20 rounded mb-4"></div>
                    <div className="flex gap-2">
                      <div className={`h-8 w-8 ${previewColors[0]} rounded`}></div>
                      <div className={`h-8 w-8 ${previewColors[1]} rounded`}></div>
                      <div className={`h-8 w-8 ${previewColors[2]} rounded`}></div>
                    </div>
                  </div>
                  {isActive && (
                    <div className="absolute top-2 right-2 bg-white rounded-full p-1">
                      <Check className="h-4 w-4 text-green-600" />
                    </div>
                  )}
                </div>
                
                {/* Theme Info */}
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-sm md:text-base">{t.name}</h3>
                    <p className="text-xs text-slate-500 mt-1">{t.description}</p>
                  </div>
                </div>
                
                {/* Color Swatches */}
                <div className="flex gap-1 mt-3">
                  {previewColors.map((color, idx) => (
                    <div 
                      key={idx} 
                      className={`h-4 w-4 md:h-5 md:w-5 rounded-full ${color} border-2 border-white shadow-sm`}
                    ></div>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Current Theme Details */}
      <Card>
        <CardHeader className="p-4 md:p-6">
          <CardTitle className="text-base md:text-lg flex items-center gap-2">
            <Sun className="h-4 w-4 md:h-5 md:w-5" />
            Current Theme Details
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 md:p-6 pt-0">
          <div className="grid gap-4 grid-cols-2 md:grid-cols-4">
            <div>
              <p className="text-xs text-slate-500 mb-1">Primary Color</p>
              <div className="flex items-center gap-2">
                <div className={`w-6 h-6 rounded ${getPreviewColors(currentTheme)[0]}`}></div>
                <span className="text-sm font-medium capitalize">{theme.colors.primary}</span>
              </div>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1">Secondary Color</p>
              <div className="flex items-center gap-2">
                <div className={`w-6 h-6 rounded ${getPreviewColors(currentTheme)[1]}`}></div>
                <span className="text-sm font-medium capitalize">{theme.colors.secondary}</span>
              </div>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1">Accent Color</p>
              <div className="flex items-center gap-2">
                <div className={`w-6 h-6 rounded ${getPreviewColors(currentTheme)[2]}`}></div>
                <span className="text-sm font-medium capitalize">{theme.colors.accent}</span>
              </div>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1">Style</p>
              <span className="text-sm font-medium">{theme.name}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tips */}
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-xs md:text-sm text-slate-600">
        <p className="font-medium text-slate-700 mb-2">💡 Theme Tips</p>
        <ul className="list-disc list-inside space-y-1">
          <li>Enable daily rotation to keep your portfolio feeling fresh for returning visitors</li>
          <li>Each theme is designed for optimal readability and professional appearance</li>
          <li>Theme changes apply instantly across all pages</li>
        </ul>
      </div>
    </div>
  );
};

export default ThemeManagement;
